import {
  createContext,
  useState,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useAuth } from 'react-oidc-context';
import { useTenant } from './TenantContext';
import { integrationsService } from '../services/integrationsService';

const ConnectionStatusContext = createContext({
  connectionStatus: null,
  isLoading: false,
  refresh: () => {},
});

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
// sessionStorage-backed so the cache survives a hard reload (F5, new tab in
// same origin). Survives mount/unmount but is cleared when the tab closes —
// per-session, never crosses sessions.
const STORAGE_KEY = 'sbd:connection-status:v1';

function readSessionCache(tenantId) {
  if (!tenantId) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.tenant !== tenantId) return null;
    if ((Date.now() - parsed.fetchedAt) >= CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionCache(tenantId, status, fetchedAt) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      tenant: tenantId, status, fetchedAt,
    }));
  } catch {
    // storage quota exceeded or disabled (private mode) — non-fatal
  }
}

function clearSessionCache() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

function ConnectionStatusProvider({ children }) {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastFetchRef = useRef(0);
  // Dedupes parallel callers (StrictMode double-mount, multiple pages calling
  // refresh() at once). Holds the in-flight promise so concurrent callers
  // await the same network roundtrip.
  const inFlightRef = useRef(null);
  const auth = useAuth();
  const tenant = useTenant();

  const fetchStatus = useCallback(async (force = false) => {
    if (!auth.user?.id_token || !tenant.tenant) return null;

    const now = Date.now();
    if (!force && connectionStatus && (now - lastFetchRef.current) < CACHE_TTL_MS) {
      return connectionStatus;
    }
    if (!force && inFlightRef.current) {
      return inFlightRef.current;
    }

    setIsLoading(true);
    const promise = (async () => {
      try {
        const result = await integrationsService.verifyAllConnections(
          auth.user.id_token,
          tenant.tenant,
        );
        const fetchedAt = Date.now();
        setConnectionStatus(result);
        lastFetchRef.current = fetchedAt;
        writeSessionCache(tenant.tenant, result, fetchedAt);
        return result;
      } catch (error) {
        console.error('ConnectionStatusProvider: Error verifying connections:', error);
        return null;
      } finally {
        setIsLoading(false);
        inFlightRef.current = null;
      }
    })();
    inFlightRef.current = promise;
    return promise;
  }, [auth.user?.id_token, tenant.tenant, connectionStatus]);

  // Mount / tenant change: prefer sessionStorage hit over a fresh verify
  // call so cross-document navigation (F5, new tab) doesn't re-hit upstream.
  useEffect(() => {
    if (tenant.isLoading || !tenant.tenant || !auth.user?.id_token) return;
    const cached = readSessionCache(tenant.tenant);
    if (cached) {
      setConnectionStatus(cached.status);
      lastFetchRef.current = cached.fetchedAt;
      return;
    }
    fetchStatus();
  }, [auth.user?.id_token, tenant.tenant, tenant.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Invalidate cache when tenant changes (not on first mount, and not on
  // null→actual hydration — both would wipe a valid cache).
  const prevTenantRef = useRef(null);
  useEffect(() => {
    if (!tenant.tenant) return;
    if (prevTenantRef.current === null) {
      prevTenantRef.current = tenant.tenant;
      return;
    }
    if (prevTenantRef.current === tenant.tenant) return;
    prevTenantRef.current = tenant.tenant;
    setConnectionStatus(null);
    lastFetchRef.current = 0;
    clearSessionCache();
  }, [tenant.tenant]);

  const refresh = useCallback(() => {
    return fetchStatus(true);
  }, [fetchStatus]);

  const context = useMemo(() => ({
    connectionStatus,
    isLoading,
    refresh,
  }), [connectionStatus, isLoading, refresh]);

  return (
    <ConnectionStatusContext.Provider value={context}>
      {children}
    </ConnectionStatusContext.Provider>
  );
}

export const useConnectionStatus = () => {
  return useContext(ConnectionStatusContext);
};

export { ConnectionStatusProvider, ConnectionStatusContext };
